import React from 'react';
import { useSelector } from 'react-redux';
import ReactGA from 'react-ga4';

import { ChannelEnum } from 'modules/auth/types/LoginRequest';
import { useSendMFACodeMutation } from 'modules/auth/service';
import { getMFAData, getSelectedSelfServiceId } from 'modules/auth/selectors';
import SelectCommunicationChannelModal from 'components/Communication/SelectCommunicationChannelModal';
import { getSelfServiceType } from 'modules/selfServices/selectors';
import { SelfServiceType } from 'modules/selfServices/types/SelfService';
import { getCommunicationData } from 'modules/communication/selectors';

interface ResendCodeModalProps {
  onClose: (isOpen: boolean) => void;
  canUseGA?: boolean;
}

const RESEND_OTP_CONFIG = {
  category: 'Resend',
  label: 'OTP Resent'
};

const ResendCodeModal: React.FC<ResendCodeModalProps> = ({ onClose, canUseGA }) => {
  const { connectionId, remainingCode } = useSelector(getMFAData);
  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const selfServiceType = useSelector(getSelfServiceType);
  const communicationData = useSelector(getCommunicationData);
  const [sendMFACode] = useSendMFACodeMutation();

  const blackListChannels = React.useMemo(() => {
    if (selfServiceType === SelfServiceType.BREAKDOWN) {
      return Object.values(ChannelEnum).filter(channel => channel !== ChannelEnum.SMS);
    }
    return undefined;
  }, [selfServiceType]);

  const handleResend = React.useCallback(async (channel: ChannelEnum) => {
    if (canUseGA) {
      ReactGA.event({ ...RESEND_OTP_CONFIG, action: `OTP has been resent by ${channel}` });
    }
    await sendMFACode({ connectionId, selfServiceId, channel }).unwrap();
  }, [connectionId, selfServiceId, sendMFACode, canUseGA]);

  return (
    <SelectCommunicationChannelModal
      data={communicationData}
      onSubmit={handleResend}
      onClose={onClose}
      blackListChannels={blackListChannels}
      maxAttemptReached={remainingCode === 0}
      title={{
        id: 'login.resend.title',
        defaultMessage: 'Resend code',
      }}
      message={{
        id: 'login.resend.tryAgain',
        defaultMessage: 'Resend the code via the same channel or switch to another channel available in your file.',
      }}
      sendToChannelMessage={{
        id: 'login.resend.resend',
        defaultMessage: 'Send to {channel}',
      }}
      withCountdown
    />
  );
};

export default ResendCodeModal;
